import { collectionOptions } from "@tanstack/db";
import { hashKey, type QueryClient } from "@tanstack/query-core";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { apiGet } from "@softmax/observatory-client/client";
import type {
  WikiPageListPublic,
  WikiPermissionsPublic,
  WikiRevisionPagePublic,
  WikiRevisionPublic,
} from "@softmax/observatory-client/types";

export type WikiIdentity = {
  wikiSlug: string;
  pageSlug: string;
  elevatedPrivileges: boolean;
};

export const wikiReadOptions = {
  staleTime: 30_000,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

export function wikiIndexCollection({
  wikiSlug,
  elevatedPrivileges,
  initialIndex,
  initialDataUpdatedAt,
}: Omit<WikiIdentity, "pageSlug"> & {
  initialIndex?: WikiPageListPublic | undefined;
  initialDataUpdatedAt?: number | undefined;
}) {
  const queryKey = [
    "wikis",
    wikiSlug,
    "index",
    { elevatedPrivileges },
  ] as const;
  return collectionOptions(hashKey(queryKey), (db) =>
    queryCollectionOptions<WikiPageListPublic["entries"][number]>({
      queryClient: db.requireDependency<QueryClient>("queryClient"),
      queryKey,
      queryFn: async ({ signal }) =>
        (
          await apiGet<WikiPageListPublic>(
            `/v2/wikis/${encodeURIComponent(wikiSlug)}/pages`,
            { signal, elevatedPrivileges },
          )
        ).entries,
      getKey: (page) => page.id,
      initialData: initialIndex?.entries,
      initialDataUpdatedAt,
      ...wikiReadOptions,
    }),
  );
}

export function wikiRevisionsCollection({
  wikiSlug,
  pageSlug,
  pageId,
  elevatedPrivileges,
}: WikiIdentity & { pageId: string }) {
  const queryKey = [
    "wikis",
    wikiSlug,
    "pages",
    pageSlug,
    "revisions",
    pageId,
    { elevatedPrivileges },
  ] as const;
  return collectionOptions(hashKey(queryKey), (db) =>
    queryCollectionOptions<WikiRevisionPublic>({
      queryClient: db.requireDependency<QueryClient>("queryClient"),
      queryKey,
      queryFn: async ({ signal }) =>
        (
          await apiGet<WikiRevisionPagePublic>(
            `/v2/wiki-pages/${encodeURIComponent(pageId)}/revisions`,
            { signal, elevatedPrivileges },
          )
        ).entries,
      getKey: (revision) => revision.id,
      ...wikiReadOptions,
    }),
  );
}

export function wikiPermissionsCollection({
  wikiSlug,
  elevatedPrivileges,
}: Omit<WikiIdentity, "pageSlug">) {
  const queryKey = [
    "wikis",
    wikiSlug,
    "permissions",
    { elevatedPrivileges },
  ] as const;
  return collectionOptions(hashKey(queryKey), (db) =>
    queryCollectionOptions<WikiPermissionsPublic>({
      queryClient: db.requireDependency<QueryClient>("queryClient"),
      queryKey,
      queryFn: async ({ signal }) => [
        await apiGet<WikiPermissionsPublic>(
          `/v2/wikis/${encodeURIComponent(wikiSlug)}/permissions`,
          { signal, elevatedPrivileges },
        ),
      ],
      getKey: () => "permissions",
      ...wikiReadOptions,
    }),
  );
}
